import React, { useState } from 'react';
import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import { Button, Stack, TextField, CircularProgress, Alert, Typography } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { bulkUpload, getFeeForecastData } from '../../../redux/features/feeForecastSlice';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import useHandleApiResponse from '../../../Hooks/useHandleApiResponse';
import { Icon } from '@iconify/react';
const BulkUpload = ({ dispatchParams }) => {
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(null);
  const [file, setFile] = useState(null);
  const [uploaded, setUploaded] = useState(false);
  const handleApiResponse = useHandleApiResponse();

  const { manualForecast, loading } = useSelector((state) => state.feeForecast);

  /* ---------------- CSV DOWNLOAD ---------------- */

  const downloadCSV = () => {
    if (!manualForecast || manualForecast.length === 0) return;

    const headers = Object.keys(manualForecast[0]);

    const csvRows = [
      headers.join(','),
      ...manualForecast.map((row) => headers.map((field) => `"${row[field] ?? ''}"`).join(',')),
    ];

    const csvString = csvRows.join('\n');

    const blob = new Blob([csvString], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = 'manual_forecast_template.csv';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /* ---------------- CSV PARSE ---------------- */

  const parseCSV = (text) => {
    const rows = text.split('\n').map((row) => row.split(','));
    const headers = rows[0];

    const data = rows.slice(1).map((row) => {
      let obj = {};

      headers.forEach((header, index) => {
        obj[header.replace(/"/g, '')] = row[index]?.replace(/"/g, '') || '';
      });

      return obj;
    });

    return data.filter((item) => Object.values(item).some((v) => v !== ''));
  };

  /* ---------------- CSV UPLOAD ---------------- */

  const handleUpload = () => {
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (e) => {
      const text = e.target.result;

      const entries = parseCSV(text);

      dispatch(
        bulkUpload({
          date: date ? dayjs(date).format('YYYY-MM-DD') : '',
          entries,
        }),
      ).then((res) => {
        handleApiResponse(res, {
          onSuccess: () => {
            setTimeout(() => {
              setOpen(false);
              setUploaded(false);
            }, 2000);

            dispatch(getFeeForecastData(dispatchParams));

            setFile(null);
            setDate(null);
            setUploaded(true);
          },
          onFailed: () => {
            console.log('Something went wrong');
          },
        });
      });
    };

    reader.readAsText(file);
  };

  return (
    <GlobalDialog
      title="Bulk Upload"
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth="sm"
      closeButton
      popupButton={
        <Button size="small" variant="contained" onClick={() => setOpen(true)}>
          Bulk Upload
        </Button>
      }
      content={
        <Stack spacing={3}>
          {/* DOWNLOAD CSV */}
          <Button
            startIcon={<Icon icon="line-md:download-loop" width="24" height="24" />}
            variant="contained"
            onClick={downloadCSV}
          >
            Download CSV Template
          </Button>

          {/* DATE PICKER */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Select Date"
              value={date}
              onChange={(newValue) => setDate(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>

          {/* FILE SELECT */}
          <Button
            startIcon={<Icon icon="material-symbols:upload" width="24" height="24" />}
            variant="outlined"
            component="label"
          >
            Select CSV File
            <input
              hidden
              type="file"
              accept=".csv"
              onChange={(e) => {
                setFile(e.target.files[0]);
                setUploaded(false);
              }}
            />
          </Button>

          {file && (
            <Typography variant="body2">
              Selected file: <b>{file.name}</b>
            </Typography>
          )}

          {/* UPLOAD BUTTON */}
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={loading || !file}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Uploading...' : 'Upload CSV'}
          </Button>

          {/* SUCCESS ALERT */}
          {uploaded && (
            <Alert severity="success">
              File <b>{file?.name}</b> uploaded successfully.
            </Alert>
          )}
        </Stack>
      }
    />
  );
};

export default BulkUpload;
